import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Shield, Menu, X, LogOut, User, Award, BarChart3 } from 'lucide-react';

const Header = ({ onStartCourse, currentUser, onLogout, onGetCertificate }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Curriculum', href: '#curriculum' },
    { label: 'Scenarios', href: '#scenarios' },
    { label: 'FAQ', href: '#faq' }
  ];

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-sm' 
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 group">
            <div className={`w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center transition-transform group-hover:scale-105`}>
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className={`font-bold text-lg transition-colors ${
                isScrolled ? 'text-slate-900' : 'text-white'
              }`}>
                WSafeTraining.com
              </span>
              <span className={`block text-xs transition-colors ${
                isScrolled ? 'text-slate-500' : 'text-slate-300'
              }`}>
                Free OSHA Training
              </span>
              <span className={`block text-[10px] transition-colors ${
                isScrolled ? 'text-slate-400' : 'text-slate-400'
              }`}>
                powered by wsafe.ai · patent pending ai technology
              </span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className={`text-sm font-medium transition-colors hover:text-emerald-500 ${
                  isScrolled ? 'text-slate-600' : 'text-slate-300'
                }`}
              >
                {link.label}
              </a>
            ))}
            
            {currentUser ? (
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={onGetCertificate}
                  className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                >
                  <Award className="w-4 h-4 mr-2" />
                  Certificate
                </Button>
                <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full p-0">
                    <Avatar className="h-10 w-10 border-2 border-emerald-500">
                      <AvatarImage src={currentUser.picture} alt={currentUser.name} />
                      <AvatarFallback className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
                        {getInitials(currentUser.name)}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <div className="flex items-center justify-start gap-2 p-2">
                    <div className="flex flex-col space-y-1 leading-none">
                      <p className="font-medium text-sm">{currentUser.name}</p>
                      <p className="text-xs text-slate-500">{currentUser.email}</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link to="/dashboard" className="flex items-center">
                      <BarChart3 className="mr-2 h-4 w-4" />
                      <span>My Progress</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={onGetCertificate}
                    className="cursor-pointer"
                  >
                    <Award className="mr-2 h-4 w-4" />
                    <span>Get Certificate</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={onLogout}
                    className="text-red-600 cursor-pointer"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </div>
            ) : (
              <Button 
                onClick={onStartCourse}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-2 rounded-lg transition-all hover:-translate-y-0.5"
              >
                Start Free Course
              </Button>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className={`w-6 h-6 ${isScrolled ? 'text-slate-900' : 'text-white'}`} />
            ) : (
              <Menu className={`w-6 h-6 ${isScrolled ? 'text-slate-900' : 'text-white'}`} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg border-t">
            <nav className="flex flex-col p-4 gap-2">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-slate-600 hover:text-emerald-500 py-2 px-4 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  {link.label}
                </a>
              ))}
              
              {currentUser ? (
                <>
                  <div className="flex items-center gap-3 py-2 px-4 border-t border-slate-100 mt-2">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={currentUser.picture} alt={currentUser.name} />
                      <AvatarFallback className="bg-emerald-500 text-white text-xs">
                        {getInitials(currentUser.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium">{currentUser.name}</p>
                      <p className="text-xs text-slate-500">{currentUser.email}</p>
                    </div>
                  </div>
                  <Link 
                    to="/dashboard"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-2 py-2 px-4 text-slate-600 hover:text-emerald-500 hover:bg-slate-50 rounded-lg transition-colors"
                  >
                    <BarChart3 className="w-4 h-4" />
                    My Progress
                  </Link>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      onLogout();
                    }}
                    className="mt-2 text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Log out
                  </Button>
                </>
              ) : (
                <Button 
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    onStartCourse();
                  }}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white mt-2"
                >
                  Start Free Course
                </Button>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
